import {
  Card,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Box,
  Flex,
  Icon,
  Text,
  HStack,
  Tag,
  Progress,
  SimpleGrid,
  VStack,
  Heading,
  Badge,
  Center,
  Card as ChakraCard
} from '@chakra-ui/react';
import { FiActivity, FiBarChart2, FiInfo, FiTrendingUp, FiClock, FiTarget, FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { Task } from '../../../types/task';
import { ActivityHeatmap } from './ActivityHeatmap';
import React from 'react';

interface ExpandedStatisticsProps {
  activityData: any[];
  productivityData: {
    tasksCreated: number;
    tasksCompleted: number;
    streak: number;
  };
  productivityInsights: {
    day: string;
    taskCount: number;
  };
  tasks: Task[];
  accentColor: string;
}

export const ExpandedStatistics = ({
  activityData,
  productivityData,
  productivityInsights,
  tasks,
  accentColor
}: ExpandedStatisticsProps) => {
  const { t } = useTranslation(['v0/common', 'v0/me']);
  const today = new Date();

  const pendingTasks = tasks.filter(t => !t.done).length;
  const completedTasks = tasks.filter(t => t.done).length;
  const overdueTasks = tasks.filter(t => {
    if (t.done || !t.dueAt) return false;
    return new Date(t.dueAt) < new Date();
  }).length;
  const totalTasks = tasks.length;
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const urgentTasksCount = tasks.filter(t => t.urgent && !t.done).length;
  const totalPendingTasks = tasks.filter(t => !t.done).length;
  const urgentPercentage = totalPendingTasks > 0 ? (urgentTasksCount / totalPendingTasks) * 100 : 0;

  return (
    <Card borderRadius="lg" mb={4} p={4}>
      <Tabs variant="soft-rounded" colorScheme="teal" size="sm">
        <TabList>
          <Tab>{t('v0/me:stats.activity')}</Tab>
          <Tab>{t('v0/me:stats.trends')}</Tab>
          <Tab>{t('v0/me:stats.insights')}</Tab>
        </TabList>

        <TabPanels mt={4}>
          <TabPanel p={0}>
            <ActivityHeatmap activityData={activityData} accentColor={accentColor} />

            <Box mt={6}>
              <Flex align="center" mb={2}>
                <Icon as={FiTrendingUp} mr={2} color={accentColor} />
                <Text fontWeight="medium">{t('v0/me:stats.productivityScore')}</Text>
              </Flex>

              <Flex align="center" justify="space-between">
                <Text>{t('v0/me:stats.lastWeek')}</Text>
                <HStack>
                  <Text fontWeight="bold">
                    {Math.min(Math.round((productivityData.tasksCompleted / Math.max(productivityData.tasksCreated, 1)) * 100), 100)}
                  </Text>
                  <Tag colorScheme="green" size="sm">
                    {productivityData.streak > 0 ? `${t('v0/me:stats.streak', '连续')}${productivityData.streak}${t('v0/me:stats.days', '天')}` : t('v0/common:todayToDo', '今日待完成')}
                  </Tag>
                </HStack>
              </Flex>
              <Progress
                value={Math.min(Math.round((productivityData.tasksCompleted / Math.max(productivityData.tasksCreated, 1)) * 100), 100)}
                size="sm"
                colorScheme="green"
                mt={2}
                mb={4}
                borderRadius="full"
              />

              <SimpleGrid columns={3} gap={4} mt={2}>
                <VStack align="flex-start">
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.tasksCreated')}</Text>
                  <Text fontWeight="bold">{productivityData.tasksCreated}</Text>
                </VStack>
                <VStack align="flex-start">
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.tasksCompleted')}</Text>
                  <Text fontWeight="bold">{productivityData.tasksCompleted}</Text>
                </VStack>
                <VStack align="flex-start">
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.streak')}</Text>
                  <Text fontWeight="bold">{productivityData.streak} {t('v0/me:stats.days')}</Text>
                </VStack>
              </SimpleGrid>
            </Box>
          </TabPanel>

          <TabPanel p={0}>
            <Box>
              <Flex align="center" mb={3}>
                <Icon as={FiBarChart2} mr={2} color={accentColor} />
                <Text fontWeight="medium">{t('v0/me:stats.taskCompletion')}</Text>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4} mb={4}>
                <Card variant="outline" p={3}>
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.pendingTasks')}</Text>
                  <Heading size="md" mb={1}>{pendingTasks}</Heading>
                  {overdueTasks > 0 && (
                    <Badge colorScheme="red">{overdueTasks} {t('v0/me:stats.overdue')}</Badge>
                  )}
                </Card>

                <Card variant="outline" p={3}>
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.tasksCompleted')}</Text>
                  <Heading size="md" mb={1}>{completedTasks}</Heading>
                  <Badge colorScheme="green">
                    {totalTasks > 0 ? `${completionRate}% ` : ''}
                    {t('v0/me:stats.completion')}
                  </Badge>
                </Card>
              </SimpleGrid>

              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <Card variant="outline" p={3}>
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.mostProductiveDay')}</Text>
                  <Text fontWeight="bold">{productivityInsights.day}</Text>
                  <HStack mt={1}>
                    <Badge colorScheme="green">{productivityInsights.taskCount} {t('v0/me:stats.tasks')}</Badge>
                  </HStack>
                </Card>

                <Card variant="outline" p={3}>
                  <Text fontSize="xs" color="gray.500">{t('v0/me:stats.recentActivity')}</Text>
                  <Text fontWeight="bold">
                    {(() => {
                      const lastCompletedTask = tasks
                        .filter(t => t.done)
                        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0];

                      if (!lastCompletedTask) return t('v0/common:noRecentCompletion', 'No recent completions');

                      const updateDate = new Date(lastCompletedTask.updatedAt);
                      if (new Date().toDateString() === updateDate.toDateString()) return t('v0/common:completedToday', 'Completed tasks today');
                      if (new Date(new Date().setDate(new Date().getDate() - 1)).toDateString() === updateDate.toDateString()) return t('v0/common:completedYesterday', 'Completed tasks yesterday');

                      const diffTime = Math.abs(today.getTime() - updateDate.getTime());
                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                      return t('v0/common:completedDaysAgo', 'Completed tasks {{count}} days ago', { count: diffDays });
                    })()}
                  </Text>
                </Card>
              </SimpleGrid>
            </Box>
          </TabPanel>

          <TabPanel p={0}>
            <Box>
              <Flex align="center" mb={3}>
                <Icon as={FiInfo} mr={2} color={accentColor} />
                <Text fontWeight="medium">{t('v0/me:stats.insights')}</Text>
              </Flex>

              {urgentTasksCount > 0 && (
                <Card variant="outline" p={3} mb={3}>
                  <HStack spacing={3}>
                    <Center bg="orange.100" borderRadius="full" p={2}>
                      <Icon as={FiClock} color="orange.500" />
                    </Center>
                    <Box>
                      <Text fontWeight="medium">{t('v0/me:stats.timeManagement')}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {urgentPercentage > 30
                          ? t('v0/me:stats.highUrgency')
                          : t('v0/me:stats.goodTimeManagement')}
                      </Text>
                    </Box>
                  </HStack>
                </Card>
              )}

              {!urgentTasksCount && (
                <Card variant="outline" p={3}>
                  <HStack spacing={3}>
                    <Center bg="blue.100" borderRadius="full" p={2}>
                      <Icon as={FiCheckCircle} color="blue.500" />
                    </Center>
                    <Box>
                      <Text fontWeight="medium">{t('v0/me:stats.overview')}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {t('v0/me:stats.goodProgress')}
                      </Text>
                    </Box>
                  </HStack>
                </Card>
              )}
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Card>
  );
};